/**
 * AskMario: drop-down UK / EU size tables shown above the size options (snippets/askmario_size-guide.liquid).
 *
 * The "UK Sizes" and "EU Sizes" links sit in the same fieldset as the panel and point to it with aria-controls. Clicking
 * a link opens the panel on its table, clicking the other link switches table, and clicking the open link closes it.
 *
 * The theme re-renders the whole variant picker block when a size is picked, so the open table is remembered per
 * panel ID and restored, without animation, when the new panel connects.
 */
const openTables = new Map();

class AskmarioSizeGuide extends HTMLElement {
  #abortController;

  connectedCallback() {
    this.#abortController = new AbortController();

    this.toggles.forEach((toggle) => {
      toggle.addEventListener('click', (event) => {
        event.preventDefault();
        const table = toggle.getAttribute('data-size-guide-table');
        this.select(this.selectedTable === table ? null : table);
      }, { signal: this.#abortController.signal });
    });

    if (openTables.has(this.id)) {
      this.select(openTables.get(this.id), { animate: false });
    }
  }

  disconnectedCallback() {
    this.#abortController.abort();
  }

  get toggles() {
    return Array.from(this.parentElement?.querySelectorAll(`[aria-controls="${CSS.escape(this.id)}"][data-size-guide-table]`) ?? []);
  }

  get selectedTable() {
    return this.hasAttribute('open') ? openTables.get(this.id) : null;
  }

  /**
   * Open the panel on the given table ("uk" or "eu"), or close it when table is null
   */
  select(table, { animate = true } = {}) {
    if (!animate) {
      this.style.transition = 'none';
    }

    if (table) {
      this.querySelectorAll('[data-size-guide-table]').forEach((element) => {
        element.hidden = element.getAttribute('data-size-guide-table') !== table;
      });

      openTables.set(this.id, table);
      this.setAttribute('open', '');
    } else {
      // The table stays visible while the panel closes
      openTables.delete(this.id);
      this.removeAttribute('open');
    }

    this.toggles.forEach((toggle) => {
      toggle.setAttribute('aria-expanded', toggle.getAttribute('data-size-guide-table') === table ? 'true' : 'false');
    });

    if (!animate) {
      this.offsetHeight; // Apply the open state before transitions come back
      this.style.transition = '';
    }
  }
}

if (!window.customElements.get('askmario-size-guide')) {
  window.customElements.define('askmario-size-guide', AskmarioSizeGuide);
}
